import React from 'react';
import './App.css';
import { BrowserRouter, Routes, Route, useLocation } from 'react-router-dom';
import { Toaster } from './components/ui/sonner';
import MarketingSite from './pages/MarketingSite';
import { AuthProvider } from './app/AuthContext';
import AuthCallback from './app/AuthCallback';
import ProtectedRoute from './app/ProtectedRoute';
import AppLayout from './app/AppLayout';
import Login from './app/Login';
import Signup from './app/Signup';
import ForgotPassword from './app/ForgotPassword';
import ResetPassword from './app/ResetPassword';
import Dashboard from './app/Dashboard';
import Coach from './app/Coach';
import PreGame from './app/PreGame';
import Journal from './app/Journal';
import Pricing from './app/Pricing';
import BillingSuccess from './app/BillingSuccess';
import Progress from './app/Progress';
import Videos from './app/Videos';
import ApexSix from './app/ApexSix';
import Book from './app/Book';
import AdminLayout from './app/AdminLayout';
import AdminVideos from './app/AdminVideos';
import AdminAbTests from './app/AdminAbTests';
import AdminReferrals from './app/AdminReferrals';
import AdminClients from './app/AdminClients';
import InviteAccept from './app/InviteAccept';
import AdminSubscribers from './app/AdminSubscribers';
import AdminGrants from './app/AdminGrants';
import AdminNewsletter from './app/AdminNewsletter';
import AdminDripStats from './app/AdminDripStats';
import ReferPage from './app/ReferPage';
import FrameworksLanding from './pages/FrameworksLanding';
import FrameworksDownload from './pages/FrameworksDownload';
import MindsetQuiz from './pages/quiz/MindsetQuiz';
import Unsubscribe from './pages/Unsubscribe';
import BootcampWaitlist from './pages/BootcampWaitlist';
// Bootcamp pages are intentionally hidden from public routing while awaiting a
// redesigned rollout. Imports are retained so the relaunch is a 5-min re-wire.
// eslint-disable-next-line no-unused-vars
import Bootcamp from './pages/Bootcamp';
// eslint-disable-next-line no-unused-vars
import BootcampThankYou from './pages/BootcampThankYou';
import Playbook from './pages/Playbook';
import PlaybookThankYou from './pages/PlaybookThankYou';
import Checklist from './pages/Checklist';
import ChecklistThankYou from './pages/ChecklistThankYou';
import PreGameLanding from './pages/PreGame';
import CoachesReset from './pages/CoachesReset';
import BioHub from './pages/BioHub';
import AdminNurture from './app/AdminNurture';
import AdminBioHub from './app/AdminBioHub';
import AdminAssessments from './app/AdminAssessments';
import AdminWorkshops from './app/AdminWorkshops';
import AssessmentSales from './pages/assessment/AssessmentSales';
import Workshops from './pages/Workshops';
import AssessmentSuccess from './pages/assessment/AssessmentSuccess';
import AssessmentFlow from './pages/assessment/AssessmentFlow';
import AssessmentResults from './pages/assessment/AssessmentResults';
import Privacy from './pages/legal/Privacy';
import Terms from './pages/legal/Terms';
import Disclaimer from './pages/legal/Disclaimer';
import BlogIndex from './pages/blog/BlogIndex';
import BlogPost from './pages/blog/BlogPost';
import AdminBlog from './app/AdminBlog';
import AdminCoachUsers from './app/AdminCoachUsers';
import Playlists from './pages/Playlists';
import AdminPlaylists from './app/AdminPlaylists';
import AdminAudioDrops from './app/AdminAudioDrops';
import AdminPush from './app/AdminPush';
import QuickMindsetHelpWidget from './components/QuickMindsetHelpWidget';
import FloatingCallPill from './components/FloatingCallPill';
import CookieConsentBanner from './components/CookieConsentBanner';
import InstallPromptBanner from './components/InstallPromptBanner';
import InstallGuide from './pages/InstallGuide';
import LockerRoom from './pages/LockerRoom';
import LockerRoomWelcome from './pages/LockerRoomWelcome';
import LockerRoomCTA from './components/LockerRoomCTA';
import AICoachTrialPopup from './components/AICoachTrialPopup';
import TealiumRouteTracker from './components/TealiumRouteTracker';
import AboutPage from './pages/About';
import PricingPage from './pages/Pricing';
import Contact from './pages/Contact';
import AthletesPage from './pages/audience/Athletes';
import ParentsPage from './pages/audience/Parents';
import CoachesPage from './pages/audience/Coaches';
import SchoolsPage from './pages/audience/Schools';
import MethodPage from './pages/audience/Method';

function RootRouter() {
  // CRITICAL: Detect OAuth callback synchronously during render to prevent race conditions.
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const location = useLocation();
  if (location.hash && location.hash.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<MarketingSite />} />
      <Route path="/app/login" element={<Login />} />
      <Route path="/app/signup" element={<Signup />} />
      <Route path="/app/invite/:token" element={<InviteAccept />} />
      <Route path="/app/forgot-password" element={<ForgotPassword />} />
      <Route path="/app/reset-password" element={<ResetPassword />} />
      <Route
        path="/app/*"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route path="dashboard" element={<Dashboard />} />
        <Route path="coach" element={<Coach />} />
        <Route path="pregame" element={<PreGame />} />
        <Route path="journal" element={<Journal />} />
        <Route path="progress" element={<Progress />} />
        <Route path="videos" element={<Videos />} />
        <Route path="apex-six" element={<ApexSix />} />
        <Route path="book" element={<Book />} />
        <Route path="pricing" element={<Pricing />} />
        <Route path="refer" element={<ReferPage />} />
        <Route path="billing/success" element={<BillingSuccess />} />
        <Route path="admin" element={<AdminLayout />}>
          <Route index element={<AdminVideos />} />
          <Route path="videos" element={<AdminVideos />} />
          <Route path="grants" element={<AdminGrants />} />
          <Route path="ab-tests" element={<AdminAbTests />} />
          <Route path="referrals" element={<AdminReferrals />} />
          <Route path="clients" element={<AdminClients />} />
          <Route path="subscribers" element={<AdminSubscribers />} />
          <Route path="newsletter" element={<AdminNewsletter />} />
          <Route path="drips" element={<AdminDripStats />} />
          <Route path="nurture" element={<AdminNurture />} />
          <Route path="bio-hub" element={<AdminBioHub />} />
          <Route path="assessments" element={<AdminAssessments />} />
          <Route path="workshops" element={<AdminWorkshops />} />
          <Route path="blog" element={<AdminBlog />} />
          <Route path="coach-users" element={<AdminCoachUsers />} />
          <Route path="playlists" element={<AdminPlaylists />} />
          <Route path="audio-drops" element={<AdminAudioDrops />} />
          <Route path="push" element={<AdminPush />} />
        </Route>
      </Route>
      <Route path="/frameworks" element={<FrameworksLanding />} />
      <Route path="/frameworks-download" element={<FrameworksDownload />} />
      <Route path="/quiz" element={<MindsetQuiz />} />
      <Route path="/unsubscribe" element={<Unsubscribe />} />
      <Route path="/bootcamp-waitlist" element={<BootcampWaitlist />} />
      {/* /bootcamp + /bootcamp/thank-you are temporarily disabled while awaiting
          a redesigned rollout. The page components, backend endpoints, admin
          tab, and tests remain intact. To re-enable, restore these two routes:
              <Route path="/bootcamp" element={<Bootcamp />} />
              <Route path="/bootcamp/thank-you" element={<BootcampThankYou />} />
       */}
      <Route path="/playbook" element={<Playbook />} />
      <Route path="/playbook/thank-you" element={<PlaybookThankYou />} />
      {/* "Train the Mind Like a Pro" IG campaign lead magnet (Phase 33) */}
      <Route path="/checklist" element={<Checklist />} />
      <Route path="/checklist/thank-you" element={<ChecklistThankYou />} />
      <Route path="/pre-game" element={<PreGameLanding />} />
      <Route path="/coaches-reset" element={<CoachesReset />} />
      <Route path="/links" element={<BioHub />} />
      {/* Premium $27 Athlete Self-Assessment (Phase 15) — hidden from main nav */}
      <Route path="/assessment" element={<AssessmentSales />} />
      <Route path="/workshops" element={<Workshops />} />
      <Route path="/assessment/success" element={<AssessmentSuccess />} />
      <Route path="/assessment/start" element={<AssessmentFlow />} />
      <Route path="/assessment/results/:token" element={<AssessmentResults />} />
      {/* Legal pages — attorney-approved copy (June 01, 2026) */}
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/terms" element={<Terms />} />
      <Route path="/disclaimer" element={<Disclaimer />} />
      {/* PWA installation guide — Phase A round 1 */}
      <Route path="/install" element={<InstallGuide />} />
      {/* The Apex Journal — internal SEO blog (Phase 17) */}
      <Route path="/blog" element={<BlogIndex />} />
      <Route path="/blog/:slug" element={<BlogPost />} />
      {/* The Locker Room — Spotify playlists (Phase 18) */}
      <Route path="/playlists" element={<Playlists />} />
      {/* The Locker Room hub — free + premium showcase (Phase 19) */}
      <Route path="/locker-room" element={<LockerRoom />} />
      <Route path="/locker-room/welcome" element={<LockerRoomWelcome />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/pricing" element={<PricingPage />} />
      {/* Phase 22 — audience-specific landing pages (information architecture
          restructure). Each page is a thin wrapper that reuses existing
          audience-relevant components. The homepage links into these instead
          of stacking everything on one long-scroll route. */}
      <Route path="/athletes" element={<AthletesPage />} />
      <Route path="/parents"  element={<ParentsPage />} />
      <Route path="/coaches"  element={<CoachesPage />} />
      <Route path="/schools"  element={<SchoolsPage />} />
      <Route path="/method"   element={<MethodPage />} />
      <Route path="/contact"  element={<Contact />} />
      <Route path="*" element={<MarketingSite />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        {/* Tealium SPA pageview emitter — must live inside BrowserRouter so
            it can read useLocation(). Skips the first render since the
            Tealium loader in public/index.html already fires the initial view. */}
        <TealiumRouteTracker />
        <RootRouter />
        {/* Phase 30 — FloatingMusicPlayer is intentionally NOT rendered here
            anymore. The music player is now triggered from the top-nav
            "Get in the Zone" button (MusicNavTrigger in Navbar.js), which
            opens a centered modal. The legacy floating player auto-opened
            on every page load, which the coach asked us to stop doing. */}
        <LockerRoomCTA />
        <AICoachTrialPopup />
        <QuickMindsetHelpWidget />
        <FloatingCallPill />
        <InstallPromptBanner />
        <CookieConsentBanner />
        <Toaster position="top-center" richColors />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
